import type { ListDef, FieldsOf } from "@app/schema";
import { GraphError, type GraphClient } from "./graph-client";
import { toQuery } from "./list-repository";

export interface DriveItem {
  id: string;
  name: string;
  size?: number;
  webUrl: string;
  lastModifiedDateTime: string;
  lastModifiedBy?: { user?: { displayName?: string } };
  folder?: { childCount: number };
  file?: { mimeType: string };
}

export interface Thumbnail {
  url: string;
  width: number;
  height: number;
}

export interface ImageItem extends DriveItem {
  thumbnails: { small?: Thumbnail; medium?: Thumbnail; large?: Thumbnail };
}

export interface DocumentWithFields<F> extends DriveItem {
  listItemId: number;
  fields: F;
}

/** Soglia oltre la quale si usa una upload session (Graph: max 4 MB per PUT semplice consigliato). */
const SIMPLE_UPLOAD_LIMIT = 4 * 1024 * 1024;
/** I chunk devono essere multipli di 320 KiB. */
const CHUNK = 320 * 1024 * 10; // 3,2 MB

/**
 * Accesso a una raccolta documenti: cartelle, upload (anche grandi), metadati.
 * I metadati sono le colonne della raccolta, lette/scritte tramite il listItem del file.
 */
export class DocumentLibrary<L extends ListDef, F extends FieldsOf<L> = FieldsOf<L>> {
  private driveId?: string;

  constructor(
    private readonly graph: GraphClient,
    private readonly siteId: string,
    readonly def: L,
  ) {}

  async drive(): Promise<string> {
    if (!this.driveId) {
      const d = await this.graph.request<{ id: string }>(
        `/sites/${this.siteId}/lists/${encodeURIComponent(this.def.displayName)}/drive?$select=id`,
      );
      this.driveId = d.id;
    }
    return this.driveId;
  }

  /** Contenuto di una cartella (path relativo alla radice della raccolta, "" = radice). */
  async list(folderPath = ""): Promise<DriveItem[]> {
    const drive = await this.drive();
    const target = folderPath ? `/root:/${encodePath(folderPath)}:` : "/root";
    return this.graph.getAll<DriveItem>(
      `/drives/${drive}${target}/children?$select=id,name,size,webUrl,lastModifiedDateTime,lastModifiedBy,folder,file`,
    );
  }

  /**
   * Immagini di una cartella con le miniature generate da SharePoint
   * (URL temporanei già autenticati, utilizzabili direttamente in <img>).
   * Se la cartella non esiste restituisce un array vuoto.
   */
  async listImages(folderPath: string): Promise<ImageItem[]> {
    const drive = await this.drive();
    try {
      const raw = await this.graph.getAll<DriveItem & { thumbnails?: Array<ImageItem["thumbnails"]> }>(
        `/drives/${drive}/root:/${encodePath(folderPath)}:/children?$expand=thumbnails&$select=id,name,size,webUrl,lastModifiedDateTime,file`,
      );
      return raw
        .filter((r) => r.file?.mimeType.startsWith("image/"))
        .map((r) => ({ ...r, thumbnails: r.thumbnails?.[0] ?? {} }));
    } catch (e) {
      if (e instanceof GraphError && e.isNotFound) return [];
      throw e;
    }
  }

  /** File con i metadati, filtrando sulle colonne della raccolta (es. RichiestaId). */
  async listWithFields(filter?: string): Promise<DocumentWithFields<F>[]> {
    const params = new URLSearchParams({ $expand: "fields,driveItem", $top: "200" });
    if (filter) params.set("$filter", filter);
    const raw = await this.graph.getAll<{
      id: string;
      fields: F;
      driveItem: DriveItem;
    }>(`/sites/${this.siteId}/lists/${encodeURIComponent(this.def.displayName)}/items?${toQuery(params)}`);
    return raw.filter((r) => r.driveItem?.file).map((r) => ({ ...r.driveItem, listItemId: Number(r.id), fields: r.fields }));
  }

  /** Crea una cartella (idempotente: se esiste la restituisce). */
  async ensureFolder(folderPath: string): Promise<DriveItem> {
    const drive = await this.drive();
    let parent = "";
    let last: DriveItem | undefined;
    for (const segment of folderPath.split("/").filter(Boolean)) {
      const parentRef = parent ? `/root:/${encodePath(parent)}:` : "/root";
      const current = parent ? `${parent}/${segment}` : segment;
      try {
        last = await this.graph.request<DriveItem>(`/drives/${drive}${parentRef}/children`, {
          method: "POST",
          body: { name: segment, folder: {}, "@microsoft.graph.conflictBehavior": "fail" },
        });
      } catch (e) {
        if (!(e instanceof GraphError && e.isConflict)) throw e;
        last = await this.graph.request<DriveItem>(`/drives/${drive}/root:/${encodePath(current)}`);
      }
      parent = current;
    }
    if (!last) throw new Error("Percorso cartella vuoto");
    return last;
  }

  /**
   * Carica un file. Per file > 4 MB usa una upload session a blocchi.
   * `onProgress` riceve la frazione caricata (0..1).
   */
  async upload(
    path: string,
    data: Blob,
    opts: { fields?: Partial<F>; conflict?: "rename" | "replace" | "fail"; onProgress?: (p: number) => void } = {},
  ): Promise<DriveItem> {
    const drive = await this.drive();
    const conflict = opts.conflict ?? "rename";
    let item: DriveItem;

    if (data.size <= SIMPLE_UPLOAD_LIMIT) {
      item = await this.graph.request<DriveItem>(
        `/drives/${drive}/root:/${encodePath(path)}:/content?@microsoft.graph.conflictBehavior=${conflict}`,
        { method: "PUT", rawBody: data, headers: { "Content-Type": data.type || "application/octet-stream" } },
      );
      opts.onProgress?.(1);
    } else {
      const session = await this.graph.request<{ uploadUrl: string }>(
        `/drives/${drive}/root:/${encodePath(path)}:/createUploadSession`,
        { method: "POST", body: { item: { "@microsoft.graph.conflictBehavior": conflict } } },
      );
      item = await uploadChunks(session.uploadUrl, data, opts.onProgress);
    }

    if (opts.fields && Object.keys(opts.fields).length) await this.setFields(item.id, opts.fields);
    return item;
  }

  /** Aggiorna i metadati (colonne della raccolta) di un file. */
  async setFields(driveItemId: string, fields: Partial<F>): Promise<void> {
    const drive = await this.drive();
    await this.graph.request(`/drives/${drive}/items/${driveItemId}/listItem/fields`, { method: "PATCH", body: fields });
  }

  /** URL di download temporaneo (pre-autenticato, breve durata). */
  async downloadUrl(driveItemId: string): Promise<string> {
    const drive = await this.drive();
    const it = await this.graph.request<{ "@microsoft.graph.downloadUrl": string }>(
      `/drives/${drive}/items/${driveItemId}?$select=id,@microsoft.graph.downloadUrl`,
    );
    return it["@microsoft.graph.downloadUrl"];
  }

  async delete(driveItemId: string): Promise<void> {
    const drive = await this.drive();
    await this.graph.request(`/drives/${drive}/items/${driveItemId}`, { method: "DELETE" });
  }
}

/** L'uploadUrl è pre-autenticato: NON va inviato l'header Authorization. */
async function uploadChunks(uploadUrl: string, data: Blob, onProgress?: (p: number) => void): Promise<DriveItem> {
  let offset = 0;
  for (;;) {
    const end = Math.min(offset + CHUNK, data.size);
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Range": `bytes ${offset}-${end - 1}/${data.size}` },
      body: data.slice(offset, end),
    });
    if (!res.ok && res.status !== 202) throw new Error(`Upload fallito (${res.status}) al byte ${offset}`);
    onProgress?.(end / data.size);
    if (res.status === 200 || res.status === 201) return (await res.json()) as DriveItem;
    offset = end;
  }
}

const encodePath = (p: string) => p.split("/").filter(Boolean).map(encodeURIComponent).join("/");
